import React from "react";
import {
  daysInMonth,
  firstOfNextMonth,
  formatMonthShort,
} from "../utils/months";

interface ProjectedUsageCardProps {
  selectedMonth: string; // YYYY-MM
  consumed: number;
  allowance: number;
}

/** Linear projection of month-end consumption given the current pace.
 * Rendered only on the current-month view; for past months UsageView omits
 * this card entirely (extrapolating finished history is meaningless).
 *
 * Visual structure mirrors SelectedMonthCard: headline + small label, a
 * progress bar (here showing projected/allowance), and a status footnote.
 * Bar fills red when projected > allowance, matching the warning treatment. */
export function ProjectedUsageCard({
  selectedMonth,
  consumed,
  allowance,
}: ProjectedUsageCardProps) {
  // Naive linear: consumed × days_in_month / days_elapsed.
  const daysElapsed = Math.max(1, new Date().getUTCDate());
  const totalDays = daysInMonth(selectedMonth);
  const projected = consumed * (totalDays / daysElapsed);
  const willOverage = projected > allowance;
  const resetDate = firstOfNextMonth(selectedMonth);
  const resetDateLabel = `by ${resetDate.toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
  })}`;
  const fmt = (n: number) =>
    n.toLocaleString(undefined, { maximumFractionDigits: 2 });

  return (
    <div className="rounded-lg border border-border bg-card shadow-sm h-full">
      <div className="p-5 flex flex-col">
        <h2 className="text-lg font-semibold text-muted-foreground text-sm uppercase">
          Projected for {formatMonthShort(selectedMonth)}
        </h2>
        <div className="mt-2 flex-1 flex flex-col justify-between">
          <div>
            <div className="flex justify-between items-end mb-2">
              <span
                className={
                  willOverage
                    ? "font-mono text-3xl font-bold text-destructive"
                    : "font-mono text-3xl font-bold text-primary"
                }
              >
                ~{fmt(projected)}
              </span>
              <span className="text-sm text-muted-foreground mb-1">
                {resetDateLabel}
              </span>
            </div>
            <progress
              className={
                willOverage
                  ? "w-full h-3 rounded-full bg-muted accent-destructive [&::-webkit-progress-bar]:rounded-full [&::-webkit-progress-bar]:bg-muted [&::-webkit-progress-value]:rounded-full [&::-webkit-progress-value]:bg-destructive [&::-moz-progress-bar]:bg-destructive"
                  : "w-full h-3 rounded-full bg-muted accent-primary [&::-webkit-progress-bar]:rounded-full [&::-webkit-progress-bar]:bg-muted [&::-webkit-progress-value]:rounded-full [&::-webkit-progress-value]:bg-primary [&::-moz-progress-bar]:bg-primary"
              }
              value={Math.min(projected, allowance)}
              max={allowance > 0 ? allowance : 1}
            ></progress>
            <div className="flex justify-between mt-2 text-xs">
              <span
                className={
                  willOverage ? "text-destructive font-semibold" : "text-muted-foreground"
                }
              >
                {willOverage
                  ? `Projected overage of ${fmt(projected - allowance)}`
                  : "On track to stay within allowance"}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
